import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../api";
import { Link } from "react-router-dom";
import { useAuth } from "../auth";
import { Briefcase, Search, MapPin, Mail, Phone, ExternalLink, ArrowLeft, Lock, X, Check } from "lucide-react";

const PROFESSION_LABELS = {
  notaire: "Notaire",
  avocat: "Avocat",
  courtier: "Courtier",
  assureur: "Assureur",
  cgp: "CGP",
  banquier: "Banquier",
  expert_comptable: "Expert-comptable",
  agent_immobilier: "Agent immobilier",
  mediateur: "Médiateur",
  geometre: "Géomètre",
  genealogiste: "Généalogiste",
  autre: "Autre",
};

export default function Annuaire() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [profession, setProfession] = useState("");
  const [codePostal, setCodePostal] = useState("");
  const [q, setQ] = useState("");
  const [contactingPro, setContactingPro] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (profession) params.profession = profession;
      if (codePostal) params.code_postal = codePostal;
      if (q) params.q = q;
      const r = await api.get("/annuaire", { params });
      setData(r.data);
    } catch (err) {
      if (err.response?.status === 402) {
        setError("paywall");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [profession, codePostal, q]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--auprea-cream)" }}>
        <div style={{ color: "rgba(35,38,46,0.5)" }}>Chargement de l'annuaire…</div>
      </div>
    );
  }

  if (error === "paywall") {
    return <Paywall />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "var(--auprea-cream)" }}>
        <div className="auprea-card max-w-md w-full">
          <h2 style={{ fontSize: 22, marginBottom: 12 }}>Erreur</h2>
          <p style={{ color: "rgba(35,38,46,0.7)", fontSize: 15 }}>{error}</p>
          <Link to="/dashboard" className="auprea-btn w-full inline-block text-center mt-4">Retour</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 md:px-12 py-10" style={{ backgroundColor: "var(--auprea-cream)" }}>
      <div className="max-w-6xl mx-auto space-y-6">
        <Link to="/dashboard" className="flex items-center gap-2" style={{ color: "var(--auprea-blue)", fontSize: 14 }} data-testid="back-link">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <div>
          <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>
            — Annuaire professionnel
          </div>
          <h1 style={{ fontSize: 36, margin: 0 }}>Trouvez le professionnel qui vous accompagne</h1>
          <p style={{ fontSize: 16, color: "rgba(35,38,46,0.7)", marginTop: 8, lineHeight: 1.6, maxWidth: 720 }}>
            Notaires, avocats, courtiers, conseillers en gestion de patrimoine : sélection de professionnels
            partenaires d'AUPREA pour vous aider dans vos démarches patrimoniales et successorales.
          </p>
        </div>

        {/* Filtres */}
        <div className="auprea-card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="auprea-label">Profession</label>
              <select className="auprea-input" value={profession} onChange={(e) => setProfession(e.target.value)} data-testid="filter-profession">
                <option value="">Toutes</option>
                {(data?.professions_available || []).map((p) => {
                  const count = data?.professions_counts?.[p] || 0;
                  return <option key={p} value={p}>{PROFESSION_LABELS[p] || p} ({count})</option>;
                })}
              </select>
            </div>
            <div>
              <label className="auprea-label">Code postal / département</label>
              <input className="auprea-input" type="text" value={codePostal} onChange={(e) => setCodePostal(e.target.value)} placeholder="Ex : 75 ou 75008" data-testid="filter-cp" />
            </div>
            <div>
              <label className="auprea-label">Recherche libre</label>
              <input className="auprea-input" type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, cabinet…" data-testid="filter-q" />
            </div>
          </div>
        </div>

        {/* Résultats */}
        <div style={{ fontSize: 13, color: "rgba(35,38,46,0.6)" }} data-testid="annuaire-count">
          {data?.total || 0} professionnel{(data?.total || 0) > 1 ? "s" : ""} trouvé{(data?.total || 0) > 1 ? "s" : ""}
        </div>

        {(!data?.partenaires || data.partenaires.length === 0) ? (
          <div className="auprea-card" style={{ textAlign: "center", padding: "48px 24px" }}>
            <Briefcase size={32} style={{ color: "rgba(35,38,46,0.3)", margin: "0 auto 12px" }} />
            <div style={{ fontSize: 16, color: "rgba(35,38,46,0.7)", marginBottom: 6 }}>Aucun professionnel correspondant</div>
            <div style={{ fontSize: 13, color: "rgba(35,38,46,0.5)" }}>Modifiez vos filtres ou recherchez sans contrainte.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="annuaire-grid">
            {data.partenaires.map((p) => (
              <ProCard key={p.id} pro={p} onContact={() => setContactingPro(p)} />
            ))}
          </div>
        )}

        {contactingPro && (
          <ContactModal pro={contactingPro} onClose={() => setContactingPro(null)} />
        )}
      </div>
    </div>
  );
}

function ProCard({ pro, onContact }) {
  return (
    <div className="auprea-card" data-testid={`pro-card-${pro.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 style={{ fontSize: 20, margin: 0, fontFamily: "Spectral, serif", fontWeight: 500, color: "var(--auprea-navy)" }}>
            {pro.prenom} {pro.nom}
          </h3>
          <div style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginTop: 2 }}>{pro.cabinet || ""}</div>
        </div>
        <span style={{ padding: "3px 10px", background: "rgba(202,168,106,0.15)", color: "#8a6f3d", fontSize: 11, borderRadius: 999, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {PROFESSION_LABELS[pro.profession] || pro.profession}
        </span>
      </div>
      {pro.bio_courte && (
        <p style={{ fontSize: 14, color: "rgba(35,38,46,0.75)", lineHeight: 1.5, margin: "10px 0" }}>{pro.bio_courte}</p>
      )}
      <div className="space-y-1 mt-3" style={{ fontSize: 13, color: "rgba(35,38,46,0.7)" }}>
        {(pro.code_postal || pro.ville) && (
          <div className="flex items-center gap-2"><MapPin size={13} /> {pro.code_postal} {pro.ville}</div>
        )}
        {pro.site_web && (
          <div className="flex items-center gap-2">
            <ExternalLink size={13} />
            <a href={pro.site_web.startsWith("http") ? pro.site_web : `https://${pro.site_web}`} target="_blank" rel="noreferrer" style={{ color: "var(--auprea-blue)" }}>{pro.site_web}</a>
          </div>
        )}
      </div>
      <button
        onClick={onContact}
        className="auprea-btn w-full mt-4"
        data-testid={`contact-pro-${pro.id}`}
      >
        Contacter ce professionnel
      </button>
    </div>
  );
}

function ContactModal({ pro, onClose }) {
  const [motif, setMotif] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!motif.trim() || !message.trim()) return;
    setSending(true);
    setError("");
    try {
      await api.post(`/annuaire/${pro.id}/contact`, { motif, message });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(21,41,79,0.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onClose}
      data-testid="contact-modal"
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", maxWidth: 540, width: "100%", padding: "32px 36px", borderRadius: 8 }}>
        <div className="flex justify-between items-start mb-2">
          <h2 style={{ fontSize: 22, margin: 0 }}>Contacter {pro.prenom} {pro.nom}</h2>
          <button onClick={onClose} aria-label="Fermer"><X size={20} style={{ color: "rgba(35,38,46,0.5)" }} /></button>
        </div>
        <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginBottom: 22 }}>
          Vos coordonnées seront transmises à {pro.prenom} {pro.nom} ({PROFESSION_LABELS[pro.profession]}) pour qu'il/elle vous recontacte directement.
        </p>
        {done ? (
          <>
            <div style={{ background: "rgba(47,125,91,0.08)", border: "1px solid rgba(47,125,91,0.3)", padding: "16px 20px", borderRadius: 6, marginBottom: 18, display: "flex", gap: 12, alignItems: "flex-start" }}>
              <Check size={20} style={{ color: "var(--auprea-green)", flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600, color: "var(--auprea-green)", fontSize: 15 }}>Message envoyé</div>
                <div style={{ fontSize: 13, color: "rgba(35,38,46,0.7)", marginTop: 4 }}>
                  {pro.prenom} {pro.nom} a reçu votre demande et vous recontactera prochainement.
                </div>
              </div>
            </div>
            <button onClick={onClose} className="auprea-btn w-full" data-testid="contact-done-close">Fermer</button>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="auprea-label">Motif de votre demande *</label>
              <input className="auprea-input" type="text" value={motif} onChange={(e) => setMotif(e.target.value)} required placeholder="Ex : Conseil sur succession" maxLength={200} data-testid="contact-motif" />
            </div>
            <div>
              <label className="auprea-label">Votre message *</label>
              <textarea className="auprea-input" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} required maxLength={3000} placeholder="Décrivez brièvement votre situation et ce que vous attendez du rendez-vous…" data-testid="contact-message" />
            </div>
            {error && (
              <div style={{ color: "#b53a3a", fontSize: 14, padding: "10px 12px", background: "#fcf3f3", border: "1px solid #f0c5c5" }} data-testid="contact-error">
                {error}
              </div>
            )}
            <button type="submit" className="auprea-btn w-full" disabled={sending} data-testid="contact-submit">
              {sending ? "Envoi…" : "Envoyer la demande de contact"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Paywall() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10" style={{ backgroundColor: "var(--auprea-cream)" }}>
      <div className="auprea-card max-w-md w-full text-center">
        <Lock size={32} style={{ color: "var(--auprea-gold)", margin: "0 auto 12px" }} />
        <h2 style={{ fontSize: 24, margin: "0 0 8px 0" }}>Annuaire professionnel</h2>
        <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600, marginBottom: 18 }}>
          Forfait Essentiel ou Confort
        </div>
        <p style={{ color: "rgba(35,38,46,0.75)", fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>
          L'accès à notre annuaire de notaires, avocats, courtiers et conseillers en gestion de patrimoine
          est inclus dans les forfaits <strong>Essentiel</strong> et <strong>Confort</strong>.
        </p>
        <Link to="/tarifs" className="auprea-btn w-full inline-block text-center" data-testid="paywall-cta">
          Découvrir les forfaits
        </Link>
        <div className="mt-4">
          <Link to="/dashboard" style={{ color: "var(--auprea-blue)", fontSize: 13 }} data-testid="paywall-back">← Retour au tableau de bord</Link>
        </div>
      </div>
    </div>
  );
}
